<script setup lang="ts">
import { computed, ComputedRef, watch, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { IRobotFinderResult, ModuleFolderMapping } from '@/models/RobotFinder';
import {
  ModuleIdNameMapping,
  RobotFinderType,
  SampleRobotConfig,
} from '@/models/Robot';
import RobotFinderResult from '@/components/robotFinder/RobotFinderResult.vue';
import useAuthStore from '@/store/AuthStore';
import useNotionStore from '@/store/NotionStore';
import { PreconfiguredRobot } from '@/models/NotionResources';
import RobotFinderService from '@/api/RobotFinderApi';
import {
  getModuleIdNameMapping,
  getModuleNamesAsCommaSeparatedString,
} from '@/helpers/RobotHelper';
import { useDisplay } from 'vuetify';
import { useLoadingSpinnerStore } from '@therobotcompany/robco-frontend-library';

const { smAndDown } = useDisplay();
const loadingSpinnerStore = useLoadingSpinnerStore();
const authStore = useAuthStore();
const notionStore = useNotionStore();
const { preconfiguredRobots } = storeToRefs(notionStore);

const searchTerm = ref('');
const reachFilter = ref<[number, number]>([0, 3000]); // [min,max] in mm
const payloadFilter = ref<[number, number]>([0, 40]); // [min,max] in kg
const sortKey = ref<'payload' | 'reach'>('payload');
const sortAsc = ref(true);
interface Props {
  latestRobotModules: ModuleFolderMapping;
}

const props = defineProps<Props>();

const allResults = ref<
  {
    config: SampleRobotConfig;
    data: IRobotFinderResult;
  }[]
>([]);

const sampleConfigurations = computed(() => {
  return (preconfiguredRobots.value || [])
    .filter(
      (robot: PreconfiguredRobot) => robot.EnglishName !== 'Dispensing Doris',
    )
    .map((robot: PreconfiguredRobot) => {
      return {
        name: authStore.language === 'de' ? robot.RobotName : robot.EnglishName,
        moduleIds: robot.IdBuildOrder.split(', '),
        price: robot.ListPrice,
      };
    });
});

const moduleIdNameMapping: ComputedRef<ModuleIdNameMapping[]> = computed(() => {
  return getModuleIdNameMapping(props.latestRobotModules);
});

async function loadAllSampleConfigs() {
  const results: { config: SampleRobotConfig; data: IRobotFinderResult }[] = [];

  const promises = sampleConfigurations.value.map(async (config) => {
    try {
      const response = await RobotFinderService.getRobotFromModuleIds(
        config.moduleIds.map((id) => parseInt(id, 10)),
      );
      results.push({
        config,
        data: {
          ...response,
          resultType: RobotFinderType.SAMPLE_CONFIG,
          module_names: getModuleNamesAsCommaSeparatedString(
            response.module_ids,
            moduleIdNameMapping.value,
          ),
        },
      });
    } catch (err) {
      console.error(`Failed for ${config.name}`, err);
    }
  });

  await Promise.all(promises);

  allResults.value = results;
}

function toggleSort(key: 'payload' | 'reach') {
  if (sortKey.value === key) {
    sortAsc.value = !sortAsc.value; // toggle direction
  } else {
    sortKey.value = key;
    sortAsc.value = true; // default ascending
  }
}

function matchesFilters(item: {
  config: SampleRobotConfig;
  data: IRobotFinderResult;
}) {
  const search = searchTerm.value.trim().toLowerCase();
  const matchesSearch =
    !search ||
    [item.data.module_names, item.config.name]
      .filter(Boolean)
      .some((v) => v?.toString().toLowerCase().includes(search));

  const withinReach =
    item.data.reach * 1000 >= reachFilter.value[0] &&
    item.data.reach * 1000 <= reachFilter.value[1];
  const withinPayload =
    item.data.payload >= payloadFilter.value[0] &&
    item.data.payload <= payloadFilter.value[1];
  return matchesSearch && withinReach && withinPayload;
}

function getOrder(entry: {
  config: SampleRobotConfig;
  data: IRobotFinderResult;
}) {
  if (!sortKey.value) {
    return 0;
  }
  // Sort by payload or reach while preserving current direction
  const sorted = [...allResults.value].sort((a, b) => {
    const diff = a.data[sortKey.value] - b.data[sortKey.value];
    return sortAsc.value ? diff : -diff;
  });
  return sorted.findIndex((e) => e === entry);
}

watch(
  sampleConfigurations,
  async (newConfigs) => {
    if (newConfigs.length) {
      loadingSpinnerStore.setShowLoadingOverlay(true);
      await loadAllSampleConfigs();
      loadingSpinnerStore.setShowLoadingOverlay(false);
    }
  },
  { immediate: true }, // also triggers if already populated
);
</script>
<template>
  <v-sheet class="pa-4" color="transparent">
    <v-row class="align-center" dense wrap>
      <!-- Search -->
      <v-col cols="12" md="3">
        <v-text-field
          v-model="searchTerm"
          :label="$gettext('Search by name or module')"
          prepend-inner-icon="mdi-magnify"
          clearable
        />
      </v-col>

      <!-- Reach Filter -->
      <v-col cols="12" md="3">
        <v-range-slider
          v-model="reachFilter"
          :class="{
            'mr-6 mt-4': smAndDown,
            'robot-catalogue-slider--de': authStore.language === 'de',
            'robot-catalogue-slider--en': authStore.language === 'en',
          }"
          min="0"
          max="3000"
          step="50"
          :label="`${$gettext('Reach')} (mm)`"
          thumb-label="always"
          @touchstart.stop
          @touchmove.stop
          @touchend.stop
        />
      </v-col>

      <!-- Payload Filter -->
      <v-col cols="12" md="3">
        <v-range-slider
          v-model="payloadFilter"
          :class="{
            'mr-6 mt-4': smAndDown,
            'robot-catalogue-slider--de': authStore.language === 'de',
            'robot-catalogue-slider--en': authStore.language === 'en',
          }"
          min="0"
          max="40"
          step="1"
          :label="`${$gettext('Payload')} (kg)`"
          thumb-label="always"
          @touchstart.stop
          @touchmove.stop
          @touchend.stop
        />
      </v-col>

      <!-- Sorting Buttons -->
      <v-col
        cols="12"
        md="3"
        class="d-flex align-center justify-end gap-2 mb-5 flex-wrap"
      >
        <v-btn
          variant="outlined"
          class="ml-1 mr-2"
          :color="sortKey === 'payload' ? 'primary-darken-1' : ''"
          @click="toggleSort('payload')"
        >
          {{ $gettext('Payload') }}
          <v-icon end>
            {{
              sortKey === 'payload'
                ? sortAsc
                  ? 'mdi-arrow-up'
                  : 'mdi-arrow-down'
                : 'mdi-swap-vertical'
            }}
          </v-icon>
        </v-btn>

        <v-btn
          variant="outlined"
          :color="sortKey === 'reach' ? 'primary-darken-1' : ''"
          @click="toggleSort('reach')"
        >
          {{ $gettext('Reach') }}
          <v-icon end>
            {{
              sortKey === 'reach'
                ? sortAsc
                  ? 'mdi-arrow-up'
                  : 'mdi-arrow-down'
                : 'mdi-swap-vertical'
            }}
          </v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <!-- Modules -->
      <v-col
        v-for="entry in allResults"
        v-show="matchesFilters(entry)"
        :key="entry.config.name"
        :class="matchesFilters(entry) ? 'd-flex' : ''"
        cols="12"
        md="6"
        lg="3"
        :style="{
          order: getOrder(entry),
        }"
      >
        <v-sheet class="pa-4" rounded="xl" elevation="2">
          <robot-finder-result
            :data="entry.data"
            :name="entry.config.name"
            :show-in-columns="true"
          />
        </v-sheet>
      </v-col>
    </v-row>
  </v-sheet>
</template>
<style lang="scss" scoped>
@media (max-width: 959px) {
  .robot-catalogue-slider--en {
    :deep(.v-input__prepend) {
      width: 120px;
    }
  }

  .robot-catalogue-slider--de {
    :deep(.v-input__prepend) {
      width: 140px;
    }
  }
}
</style>
